import * as React from 'react';
import { ChevronRight, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ORDER_STATUS_REASON, ORDER_UPDATE_FIELD_CATEGORY, type Order } from '@klareospace/pharos-lib-types';
import { Button, DrawerSection, ErrorBanner } from '@klareospace/pharos-lib-web-utils';

import { hasAnomalyReason, translateStatusReason } from '../utils';
import { useOrderEditability } from '../hooks/use-order-editability';
import { HandlingUnitQuantityInlineEditor } from './handling-unit-quantity-inline-editor';
import { PriceInlineEditor } from './price-inline-editor';
import { TagsInlineEditor } from './tags-inline-editor';
import { VolumeInlineEditor } from './volume-inline-editor';
import { WeightInlineEditor } from './weight-inline-editor';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ContentSummarySectionProps {
  readonly onViewMore: () => void;
  readonly order: Order;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ContentSummarySection({ onViewMore, order }: ContentSummarySectionProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { i18n, t } = useTranslation('orders');
  const { getFieldState, lockReason } = useOrderEditability(order);

  const isMetricsError = hasAnomalyReason(order, ORDER_STATUS_REASON.METRICS_VALIDATION_FAILED);
  const impactingFieldState = getFieldState(ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING);
  const nonImpactingFieldState = getFieldState(ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING);
  const metricsErrorMessage = translateStatusReason(t, ORDER_STATUS_REASON.METRICS_VALIDATION_FAILED) ?? '';

  const headerRight = (
    <Button className="h-7 px-2 text-xs gap-1" onClick={onViewMore} variant="ghost">
      {t('drawer.viewDetail')}
      <ChevronRight className="w-3 h-3" />
    </Button>
  );

  return (
    <DrawerSection
      banner={isMetricsError ? <ErrorBanner message={metricsErrorMessage} /> : undefined}
      headerRight={headerRight}
      icon={Package}
      rows={[
        {
          cells: [
            {
              cell: (
                <HandlingUnitQuantityInlineEditor
                  fieldState={impactingFieldState}
                  isError={isMetricsError}
                  lockReason={lockReason}
                  order={order}
                />
              ),
              label: tCommon('fields.handlingUnits'),
            },
            {
              cell: (
                <WeightInlineEditor
                  fieldState={impactingFieldState}
                  isError={isMetricsError}
                  lockReason={lockReason}
                  order={order}
                />
              ),
              label: tCommon('fields.weight'),
            },
            {
              cell: (
                <VolumeInlineEditor
                  fieldState={impactingFieldState}
                  isError={isMetricsError}
                  lockReason={lockReason}
                  order={order}
                />
              ),
              label: tCommon('fields.volume'),
            },
          ],
        },
        {
          cells: [
            {
              cell: <TagsInlineEditor fieldState={nonImpactingFieldState} lockReason={lockReason} order={order} />,
              label: t('drawer.tags'),
            },
            {
              cell: (
                <PriceInlineEditor
                  fieldState={nonImpactingFieldState}
                  locale={i18n.language}
                  lockReason={lockReason}
                  order={order}
                />
              ),
              label: tCommon('fields.value'),
            },
          ],
          columns: 3,
        },
      ]}
      title={t('drawer.contentSummary')}
    />
  );
}
